import { Button } from '@zeiss/beyond-online-react';
import {
  Box,
  ButtonGroup,
  PageHeader,
  StepItem,
  Stepper,
} from '@zeiss/lib-components';
import { displayFieldError } from '@zeiss/lib-helpers';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { defaultValues } from './defaultValues';
import { useAppForm } from './form';
import { InvitationCreateFormValues, StepConfig } from './types';
import { buildCreateInvitationsRequest } from './utils';
import { getStepConfigs } from './wizardConfig';
import { showToast } from 'src/helpers/showToast';
import { useCreateInvitations } from 'src/hooks/api';
import { CreateInvitationResponse } from 'src/types/rest/managementPortalApi';

export const InvitationCreate = () => {
  const { i18n, t } = useTranslation(['common', 'invitations']);
  const navigate = useNavigate();

  const [createdInvitation, setCreatedInvitation] =
    useState<CreateInvitationResponse | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  const stepConfigs = useMemo(() => getStepConfigs(t), [i18n.language, t]);

  const {
    isPending: isCreateInvitationPending,
    mutateAsync: createInvitations,
  } = useCreateInvitations();

  const form = useAppForm({
    defaultValues,
    onSubmit: () => undefined,
  });

  const cancel = () => {
    navigate('..', { relative: 'path' });
  };

  const isSummaryStep =
    createdInvitation !== null && stepIndex === stepConfigs.length - 1;

  const handleStepRequest = (requestedStepIndex: number) => {
    if (createdInvitation || isCreateInvitationPending) {
      return;
    }

    if (requestedStepIndex <= stepIndex) {
      setStepIndex(requestedStepIndex);
    }
  };

  /**
   * Submit the final wizard values and return the created invitation for the
   * summary step.
   */
  const createInvitation = async (values: InvitationCreateFormValues) => {
    const invitationType = values.step1.invitationType;
    const invitationTarget = values.step1.invitationFor;

    if (!invitationType || !invitationTarget) {
      return null;
    }

    const response = await createInvitations(
      buildCreateInvitationsRequest(invitationType, {
        additionalData: values.step3.additionalData,
        email: values.step2.email,
        firstName: values.step2.firstName,
        lastName: values.step2.lastName,
        locale: values.step2.locale,
        location: values.step2.location,
        targetId: invitationTarget.id,
      }),
    );

    const createdInvitation = response.invitations[0];

    if (!createdInvitation) {
      showToast({
        props: {
          title: t('common:notification.somethingWentWrong'),
          type: 'error',
        },
      });

      return null;
    }

    return createdInvitation;
  };

  /**
   * Advance regular steps immediately, but only move past the confirmation step
   * after creation succeeds.
   */
  const submitStep = async (
    step: StepConfig,
    index: number,
    values: InvitationCreateFormValues,
  ) => {
    if (step.key !== 'step4') {
      setStepIndex(index + 1);

      return;
    }

    try {
      const createdInvitation = await createInvitation(values);

      if (!createdInvitation) {
        return;
      }

      setCreatedInvitation(createdInvitation);
      setStepIndex(index + 1);
    } catch {
      showToast({
        props: {
          description: t('common:text.pleaseTryAgain'),
          title: t('invitations:notification.invitationCreationError'),
          type: 'error',
        },
      });
    }
  };

  return (
    <>
      <PageHeader
        headline={
          isSummaryStep
            ? t('invitations:title.invitationCreated')
            : t('common:action.newX', {
                x: t('common:glossary.invitation'),
              })
        }
      />

      <Box spacing="2xl">
        <form.AppForm>
          <Stepper
            activeStepIndex={stepIndex}
            completedStepIndex={stepIndex - 1}
            onStepRequest={handleStepRequest}
            responsiveness="optimal"
            sticky
            variant="numeric"
          >
            {stepConfigs.map((step, index) => {
              const isFirstStep = index === 0;
              const isLastStep = index === stepConfigs.length - 1;
              const StepFields = step.Component;

              return (
                <StepItem key={step.key} label={step.label}>
                  <Box>
                    <StepFields
                      createdInvitation={createdInvitation}
                      displayFieldError={displayFieldError}
                    />
                  </Box>

                  <Box>
                    {isSummaryStep && isLastStep ? null : (
                      <ButtonGroup spacing="s" variant="form">
                        <Button
                          disabled={isCreateInvitationPending}
                          onClick={
                            isFirstStep ? cancel : () => setStepIndex(index - 1)
                          }
                          variant="secondary"
                        >
                          {isFirstStep
                            ? t('common:action.cancel')
                            : t('common:action.back')}
                        </Button>
                        <form.Subscribe selector={(state) => state.values}>
                          {(values) => (
                            <Button
                              disabled={
                                isCreateInvitationPending ||
                                (step.canAdvance
                                  ? !step.canAdvance(values)
                                  : false)
                              }
                              loading={
                                isCreateInvitationPending &&
                                step.key === 'step4'
                              }
                              onClick={() => submitStep(step, index, values)}
                            >
                              {t('common:action.next')}
                            </Button>
                          )}
                        </form.Subscribe>
                      </ButtonGroup>
                    )}
                  </Box>
                </StepItem>
              );
            })}
          </Stepper>
        </form.AppForm>
      </Box>
    </>
  );
};
